import * as React from "react";

import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import type { AuditActorCandidate } from "@/hooks/queries/admin-audit-types";
import { useAuditEventNames } from "@/hooks/queries/useAuditEventNames";
import {
  ACTOR_CANDIDATE_LIMIT,
  useAuditActorSearch,
} from "@/hooks/queries/useQueryAuditLog";
import { cn } from "@/lib/utils";

// Story 11.7 (AC9, contract rows 3-6) — the audit console's filter bar.
//
// Four controls: a From/To date pair, the event-name combobox fed from the store's
// own vocabulary, an actor picker that resolves an e-mail to a UUID, and a raw
// organization-id input. Tokens only; no hardcoded hex.

/**
 * How long the actor search waits after the last keystroke. Short enough that a
 * pause between words does not feel like a stall, long enough that typing an
 * address does not fire a request per character. No named source — no debounce
 * helper exists in this codebase, so this value is chosen here (Dev Agent Record).
 */
export const ACTOR_SEARCH_DEBOUNCE_MS = 300;

/**
 * Characters required before the actor search fires. One character would ask the
 * server for most of the directory and return a list nobody can use. No named
 * source — see the Dev Agent Record.
 */
export const ACTOR_SEARCH_MIN_CHARS = 2;

/**
 * How long the organization-id field waits after the last keystroke before its value
 * is lifted into the query. Deliberately the SAME pause as the actor search rather
 * than a second tuned number: both are free-text filters in one bar, and two
 * different pauses would read as one of the two lagging.
 */
export const ORG_ID_DEBOUNCE_MS = ACTOR_SEARCH_DEBOUNCE_MS;

export interface AuditFilterState {
  /** `yyyy-mm-dd` from the date inputs, or "" when unbounded. */
  from: string;
  to: string;
  eventName: string;
  /** The resolved actor UUID, or "" when no actor is selected. */
  actorId: string;
  /** The chosen actor's address — display only; never sent to /admin/audit. */
  actorLabel: string;
  orgId: string;
}

export const EMPTY_AUDIT_FILTERS: AuditFilterState = {
  from: "",
  to: "",
  eventName: "",
  actorId: "",
  actorLabel: "",
  orgId: "",
};

export function AuditLogFilterBar({
  filters,
  onChange,
  dateRangeError,
}: {
  filters: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
  /** Message for the To field when the API refused the range (ADMIN-010). */
  dateRangeError?: string;
}) {
  const eventNames = useAuditEventNames();
  const options = React.useMemo(
    () => (eventNames.data?.event_names ?? []).map((name) => ({ value: name, label: name })),
    [eventNames.data],
  );

  function set<K extends keyof AuditFilterState>(key: K, value: AuditFilterState[K]) {
    onChange({ ...filters, [key]: value });
  }

  return (
    <section aria-label="Audit filters" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label="From" htmlFor="audit-from">
        <Input
          id="audit-from"
          type="date"
          value={filters.from}
          onChange={(e) => set("from", e.target.value)}
        />
      </FormField>

      <FormField label="To" htmlFor="audit-to" error={dateRangeError}>
        <Input
          id="audit-to"
          type="date"
          value={filters.to}
          onChange={(e) => set("to", e.target.value)}
          aria-invalid={dateRangeError ? true : undefined}
        />
      </FormField>

      <FormField
        label="Event"
        htmlFor="audit-event-name"
        helper="Only events the platform has actually recorded are listed."
      >
        <SearchableCombobox
          id="audit-event-name"
          options={options}
          value={filters.eventName}
          onChange={(value) => set("eventName", value)}
          placeholder="Any event"
          searchAriaLabel="Search event names"
          clearAriaLabel="Clear event name"
          emptyText="No event names recorded yet"
          disabled={eventNames.isPending}
        />
      </FormField>

      <ActorFilter
        actorId={filters.actorId}
        actorLabel={filters.actorLabel}
        onPick={(actorId, actorLabel) => onChange({ ...filters, actorId, actorLabel })}
      />

      <OrgIdFilter value={filters.orgId} onChange={(orgId) => set("orgId", orgId)} />
    </section>
  );
}

/**
 * Raw organization UUID → the `org_id` filter.
 *
 * Debounced by the same mechanism and the same pause as the actor search below, for
 * a sharper reason: this value is lifted straight into the audit query key, so an
 * undebounced field is one `/admin/audit` request per keystroke — and every one of
 * those reads is itself audited, so typing a 36-character id would write ~36 rows
 * into the store being queried.
 *
 * The parent stays the single source of truth: `value` is the APPLIED filter and
 * `draft` is only what has been typed since. `liftedRef` remembers the last value
 * this field pushed up, which is what tells an echo of our own lift apart from an
 * external change (e.g. a future "Clear filters") — without it the stale draft would
 * immediately undo a reset.
 */
function OrgIdFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (orgId: string) => void;
}) {
  const [draft, setDraft] = React.useState(value);
  const [debounced, setDebounced] = React.useState(value);
  const liftedRef = React.useRef(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(draft), ORG_ID_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  // Lift the settled value. Guarded on `liftedRef` rather than on `value` so a
  // re-render that only changed the `onChange` identity cannot re-fire it.
  React.useEffect(() => {
    if (debounced === liftedRef.current) return;
    liftedRef.current = debounced;
    onChange(debounced);
  }, [debounced, onChange]);

  // Adopt a value changed from OUTSIDE (never our own lift echoing back).
  React.useEffect(() => {
    if (value === liftedRef.current) return;
    liftedRef.current = value;
    setDraft(value);
    setDebounced(value);
  }, [value]);

  return (
    <FormField
      label="Organization ID"
      htmlFor="audit-org-id"
      helper="Matches the acting user's organization or the organization the event is about."
    >
      <Input
        id="audit-org-id"
        type="text"
        placeholder="Organization UUID"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
    </FormField>
  );
}

/** Stable id for the candidate listbox — one actor picker per bar, so a constant. */
const ACTOR_LISTBOX_ID = "audit-actor-listbox";

/**
 * Actor e-mail → `actor_id`. The address is resolved to a UUID here and only the
 * UUID is ever sent to `/admin/audit` — which is what keeps an address out of that
 * endpoint's URL, its logs and its meta-audit payload by construction (AC3).
 *
 * A11y: the WAI-ARIA 1.2 *editable* combobox pattern (list autocomplete) — the same
 * pattern searchable-combobox.tsx implements for a select-only trigger, but the
 * editable variant, because this field's text IS the query and the candidate list
 * comes from the server. The textbox carries role="combobox" + aria-expanded +
 * aria-controls and drives aria-activedescendant over the listbox; options carry a
 * real id, tabIndex={-1} and an aria-selected that actually tracks the active row;
 * ArrowUp/Down/Home/End move it, Enter picks, Escape dismisses without erasing what
 * was typed (ArrowDown re-opens).
 */
function ActorFilter({
  actorId,
  actorLabel,
  onPick,
}: {
  actorId: string;
  actorLabel: string;
  onPick: (actorId: string, actorLabel: string) => void;
}) {
  const [term, setTerm] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const [dismissed, setDismissed] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), ACTOR_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const search = useAuditActorSearch(debounced, ACTOR_SEARCH_MIN_CHARS);
  // Memoised on the query's data so the reset effect below keys off a STABLE
  // identity: a fresh `.slice()` every render would re-run it on every render and
  // pin the highlight to row 0, silently breaking the Arrow keys.
  const candidates = React.useMemo(
    () => (search.data?.results ?? []).slice(0, ACTOR_CANDIDATE_LIMIT),
    [search.data],
  );
  const searching = debounced.trim().length >= ACTOR_SEARCH_MIN_CHARS;
  const open = searching && !dismissed;

  // A new candidate set must never leave the highlight on a stale row.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [candidates]);

  // Close on outside pointer-down, the same shape as searchable-combobox.tsx: the
  // listener is on mousedown rather than blur so an option click still lands, and it
  // is bound to the WRAPPER so a press on an option counts as inside.
  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setDismissed(true);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
  }, [open]);

  const activeOptionId =
    open && candidates.length ? `${ACTOR_LISTBOX_ID}-opt-${activeIndex}` : undefined;

  function pick(candidate: AuditActorCandidate) {
    setTerm("");
    setDebounced("");
    setDismissed(false);
    onPick(candidate.user_id, candidate.email);
  }

  function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      // Dismiss the popup, keep the typed term — the operator is mid-word, not
      // starting over. (APG: a second Escape clears the textbox; here the term is
      // also the query, so clearing it is left to the field's own clear affordance.)
      event.preventDefault();
      setDismissed(true);
      return;
    }
    if (event.key === "ArrowDown" && searching && dismissed) {
      // Re-open what Escape dismissed, without retyping (APG: ArrowDown opens).
      event.preventDefault();
      setDismissed(false);
      return;
    }
    if (!open || candidates.length === 0) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % candidates.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + candidates.length) % candidates.length);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(candidates.length - 1);
        break;
      case "Enter":
        event.preventDefault();
        if (candidates[activeIndex]) pick(candidates[activeIndex]);
        break;
      default:
        break;
    }
  }

  if (actorId) {
    return (
      <FormField label="Actor e-mail" htmlFor="audit-actor-chip">
        <div
          id="audit-actor-chip"
          className="flex items-center justify-between gap-2 rounded-md border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] px-[14px] py-[10px] text-md text-[color:var(--text-primary)]"
        >
          <span className="truncate">{actorLabel || actorId}</span>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Clear actor filter"
            onClick={() => {
              setTerm("");
              setDebounced("");
              setDismissed(false);
              onPick("", "");
            }}
          >
            Remove
          </Button>
        </div>
      </FormField>
    );
  }

  return (
    <FormField
      label="Actor e-mail"
      htmlFor="audit-actor-search"
      helper={`Type at least ${ACTOR_SEARCH_MIN_CHARS} characters, then pick a person.`}
    >
      <div
        ref={wrapperRef}
        className="relative"
        // Pointer-down cannot see a Tab away, so this leg covers that, and only that:
        // a Tab always NAMES the element gaining focus, and the options are
        // tabIndex={-1}, so a non-null relatedTarget outside the wrapper is the
        // operator moving on. A null relatedTarget is deliberately not a dismissal —
        // the browser reports null whenever focus lands on nothing focusable, which
        // here means a press on the listbox's own padding or on the "Searching…" /
        // "No matching user" row, or a switch to another tab or window. Outside
        // CLICKS need no cover from this leg: the mousedown listener above holds them.
        onBlur={(event) => {
          const nextFocus = event.relatedTarget as Node | null;
          if (nextFocus && !event.currentTarget.contains(nextFocus)) {
            setDismissed(true);
          }
        }}
      >
        <Input
          id="audit-actor-search"
          type="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          // Only while the listbox exists: an aria-controls pointing at an id that is
          // not in the DOM is a dangling reference (same posture as the payload
          // toggle in AuditLogTable).
          aria-controls={open ? ACTOR_LISTBOX_ID : undefined}
          aria-activedescendant={activeOptionId}
          autoComplete="off"
          placeholder="Search by e-mail or name"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            // Typing re-opens a popup the operator dismissed with Escape.
            setDismissed(false);
          }}
          onKeyDown={onSearchKeyDown}
        />
        {open ? (
          <div
            id={ACTOR_LISTBOX_ID}
            role="listbox"
            aria-label="Matching users"
            className="absolute z-20 mt-1 max-h-[240px] w-full overflow-auto rounded-md border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-1 shadow-lg"
          >
            {search.isPending ? (
              <div role="status" className="px-[12px] py-[8px] text-sm text-[color:var(--text-tertiary)]">
                Searching…
              </div>
            ) : candidates.length === 0 ? (
              <div role="status" className="px-[12px] py-[8px] text-sm text-[color:var(--text-tertiary)]">
                No matching user
              </div>
            ) : (
              candidates.map((candidate, index) => (
                // Keyboard activation is driven from the textbox via
                // aria-activedescendant, so the option is OUT of the tab order
                // (tabIndex={-1}) while staying a real <button> for the mouse.
                <button
                  type="button"
                  key={candidate.user_id}
                  id={`${ACTOR_LISTBOX_ID}-opt-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === activeIndex}
                  className={cn(
                    "flex w-full cursor-pointer flex-col items-start rounded-sm px-[12px] py-[8px] text-left text-md text-[color:var(--text-primary)] hover:bg-[color:var(--bg-tertiary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]",
                    index === activeIndex && "bg-[color:var(--bg-tertiary)]",
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(candidate)}
                >
                  <span className="truncate">{candidate.email}</span>
                  {candidate.first_name || candidate.last_name ? (
                    <span className="text-sm text-[color:var(--text-tertiary)]">
                      {[candidate.first_name, candidate.last_name].filter(Boolean).join(" ")}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </FormField>
  );
}
